import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Button, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  AlertOutlined,
  SafetyOutlined,
  MessageOutlined,
  BarChartOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  RadarChartOutlined,
  EyeOutlined,
  ExperimentOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useAuthActions } from '../../hooks/useAuthStore';
import { getRoleDisplayName, getRoleColor } from '@shared/utils';
import { ThemeToggle } from '../theme/ThemeToggle';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAuth();
  const { logout } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();

  // Notification menu items
  const notificationMenuItems: MenuProps['items'] = [
    {
      key: 'alert1',
      label: (
        <div style={{ width: 280, padding: '8px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>🚨 Yüksek Risk Bölgesi</div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Taksim bölgesinde güvenlik riski tespit edildi
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>2 dakika önce</div>
        </div>
      ),
    },
    {
      key: 'alert2', 
      label: (
        <div style={{ width: 280, padding: '8px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>📞 Taciz Arama Artışı</div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Kadıköy bölgesinde telefon tacizi vakaları artış gösterdi
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>15 dakika önce</div>
        </div>
      ),
    },
    {
      key: 'ai-update',
      label: (
        <div style={{ width: 280, padding: '8px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>🤖 AI Model Güncellendi</div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Tehdit tespit modeli v2.1 başarıyla deploy edildi
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>1 saat önce</div>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'view-all',
      label: (
        <div style={{ textAlign: 'center', color: '#1890ff', fontWeight: 500 }}>
          Tüm Bildirimleri Görüntüle
        </div>
      ),
      onClick: () => {
        navigate('/notifications');
      },
    },
  ];

  // Emergency alert menu items
  const emergencyMenuItems: MenuProps['items'] = [
    {
      key: 'active-emergency',
      label: (
        <div style={{ width: 280, padding: '8px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#ff4d4f' }}>
            🆘 Aktif Acil Durum
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Beşiktaş'ta kayıp kişi aranıyor - ID: #EM2024-001
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>Şu anda aktif</div>
        </div>
      ),
    },
    {
      key: 'safety-alert',
      label: (
        <div style={{ width: 280, padding: '8px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#fa8c16' }}>
            ⚠️ Güvenlik Uyarısı
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Mecidiyeköy bölgesinde şüpheli aktivite
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>5 dakika önce</div>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      key: 'emergency-contacts',
      label: (
        <div style={{ textAlign: 'center', color: '#ff4d4f', fontWeight: 500 }}>
          🚨 Acil Durum Merkezini Ara
        </div>
      ),
      onClick: () => {
        console.log('Call emergency center');
      },
    },
  ];

  // Menu items based on user permissions
  const menuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Ana Sayfa',
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: 'Kullanıcı Yönetimi',
    },
    {
      key: '/emergency',
      icon: <AlertOutlined />,
      label: 'Acil Durum Merkezi',
    },
    {
      key: '/otp',
      icon: <SafetyOutlined />,
      label: 'OTP İzleme',
    },
    {
      key: '/messaging',
      icon: <MessageOutlined />,
      label: '💬 Mesajlaşma',
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Bildirimler',
    },
    {
      key: '/analytics',
      icon: <BarChartOutlined />,
      label: 'Analitik & Raporlar',
    },
    {
      key: '/ai',
      icon: <RobotOutlined />,
      label: '🧠 AI Systems',
      children: [
        {
          key: '/ai/radar',
          icon: <RadarChartOutlined />,
          label: '📡 RADAR AI Dashboard',
        },
        {
          key: '/ai/patterns',
          icon: <EyeOutlined />,
          label: '🔄 Real-time Learning',
        },
        {
          key: '/ai/anomaly',
          icon: <ExclamationCircleOutlined />,
          label: '🌐 Internet Learning',
        },
        {
          key: '/ai/models',
          icon: <ExperimentOutlined />,
          label: '📊 Scheduled Training',
        },
      ],
    },
    {
      key: '/safety-intelligence',
      icon: <SafetyOutlined />,
      label: '🚨 Safety Intelligence',
    },
    {
      key: '/beenetwork',
      icon: <TeamOutlined />,
      label: '🐝 BeeNetwork Mesh',
    },
  ];

  // User dropdown menu
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profil Ayarları',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Sistem Ayarları',
      disabled: !user?.permissions.includes('system.read'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış Yap',
      onClick: handleLogout,
    },
  ];

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        theme="light"
        width={280}
        collapsedWidth={80}
        style={{
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.06)',
          borderRight: '1px solid #f0f0f0'
        }}
      >
        {/* Logo */}
        <div style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '0' : '0 24px',
          borderBottom: '1px solid #f0f0f0',
          background: 'linear-gradient(135deg, #FF6B9D, #C44569)',
          color: 'white'
        }}>
          <div style={{
            fontSize: collapsed ? '20px' : '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '24px' }}>🎯</span>
            {!collapsed && <span>GetMyHive OpsCenter</span>}
          </div>
        </div>

        {/* Menu */}
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ 
            borderRight: 'none',
            marginTop: '8px'
          }}
        />
      </Sider>

      <Layout>
        {/* Header */}
        <Header style={{
          background: '#fff',
          padding: '0 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
        }}>
          {/* Left side */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
          </div>

          {/* Right side */}
          <Space size="middle">
            {/* Premium Theme Toggle */}
            <ThemeToggle type="dropdown" size="middle" />

            {/* Notifications */}
            <Dropdown
              menu={{ items: notificationMenuItems }}
              placement="bottomRight"
              arrow
              overlayStyle={{ zIndex: 9999 }}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
            >
              <Badge count={3}>
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  style={{ fontSize: '16px' }}
                />
              </Badge>
            </Dropdown>

            {/* Emergency Alert */}
            <Dropdown
              menu={{ items: emergencyMenuItems }}
              placement="bottomRight"
              arrow
              overlayStyle={{ zIndex: 9999 }}
              getPopupContainer={(trigger) => trigger.parentElement || document.body}
            >
              <Badge dot>
                <Button
                  type="text"
                  icon={<ExclamationCircleOutlined />}
                  style={{ color: '#ff4d4f', fontSize: '16px' }}
                />
              </Badge>
            </Dropdown>

            {/* User Info */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '8px',
                transition: 'background 0.2s',
                ':hover': {
                  background: '#f5f5f5'
                }
              }}>
                <Avatar
                  style={{
                    backgroundColor: getRoleColor(user?.role || 'analyst'),
                    color: 'white'
                  }}
                  icon={<UserOutlined />}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Text strong style={{ fontSize: '14px' }}>
                    {user?.username || 'Admin'}
                  </Text>
                  <Text 
                    type="secondary" 
                    style={{ 
                      fontSize: '12px',
                      color: getRoleColor(user?.role || 'analyst')
                    }}
                  >
                    {getRoleDisplayName(user?.role || 'analyst')}
                  </Text>
                </div>
              </div>
            </Dropdown>
          </Space>
        </Header>

        {/* Content */}
        <Content style={{
          margin: '24px',
          padding: '24px',
          background: '#fff',
          borderRadius: '8px',
          minHeight: 'calc(100vh - 112px)',
          overflow: 'auto'
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
